import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authService, AuthUser } from '../services/auth'

interface AdminContextType {
  isAuthenticated: boolean
  user: AuthUser | null
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  hasPermission: (permission: string) => boolean
  isAdmin: () => boolean
}

const AdminContext = createContext<AdminContextType | undefined>(undefined)

export function AdminProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null) // null = loading
  const [user, setUser] = useState<AuthUser | null>(null)

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Load user from stored session first
        await authService.loadUserFromSession()
        
        const currentUser = authService.getCurrentUser()
        const authenticated = authService.isAuthenticated()
        
        setUser(currentUser)
        setIsAuthenticated(authenticated)
      } catch (error) {
        console.error('Session check failed:', error)
        authService.logout()
        setUser(null)
        setIsAuthenticated(false)
      }
    }
    
    // Initial check
    checkAuth()
    
    // Check auth status every minute (non-async for interval)
    const interval = setInterval(() => {
      const currentUser = authService.getCurrentUser()
      const authenticated = authService.isAuthenticated()
      setUser(currentUser)
      setIsAuthenticated(authenticated)
    }, 60000)
    
    return () => clearInterval(interval)
  }, [])

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const authUser = await authService.login(username, password)
      if (authUser) {
        setUser(authUser)
        setIsAuthenticated(true)
        return true
      }
      return false
    } catch (error) {
      console.error('Login failed:', error)
      return false
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      console.error('Logout failed:', error)
    }
    setUser(null)
    setIsAuthenticated(false)
  }

  const hasPermission = (permission: string): boolean => {
    return authService.hasPermission(permission)
  }

  const isAdmin = (): boolean => {
    return authService.isAdmin()
  }

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <AdminContext.Provider value={{ 
      isAuthenticated: isAuthenticated || false, 
      user,
      login, 
      logout, 
      hasPermission, 
      isAdmin 
    }}>
      {children}
    </AdminContext.Provider>
  )
}

export function useAdmin() {
  const context = useContext(AdminContext)
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider')
  }
  return context
}